// Hook partilhado para auto-preencher a "Morada" a partir do Código Postal (CTT).
//
// Uso:
//   const { loading, error } = usePostalAutofill(postalCode, morada, setMorada);
//
// Regras:
// - Só chama a API se o CP tiver formato válido XXXX-XXX.
// - Debounce de 350 ms para evitar chamar a API a cada tecla.
// - Só substitui a Morada se estiver vazia OU se o utilizador tiver acabado
//   de escrever o CP (fluxo automático). Uma vez editada manualmente pelo
//   utilizador (`userEdited=true`), o hook nunca sobrepõe.
// - Se o utilizador apagar o CP, mantém a Morada existente (não limpa).

import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { isValidPostalPT } from "@/lib/formValidation";

const API = process.env.REACT_APP_BACKEND_URL;

export function usePostalAutofill(postalCode, morada, setMorada, opts = {}) {
  const { userEdited = false, enabled = true } = opts;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const lastLookupRef = useRef("");
  const lastMoradaSetByHookRef = useRef("");

  useEffect(() => {
    if (!enabled) return;
    const cp = (postalCode || "").trim();
    if (!isValidPostalPT(cp)) {
      setError("");
      return;
    }
    if (cp === lastLookupRef.current) return;

    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        setLoading(true);
        setError("");
        const url = `${API}/api/postal-codes/${encodeURIComponent(cp)}`;
        const { data } = await axios.get(url);
        if (cancelled) return;
        lastLookupRef.current = cp;
        const suggestion = data?.primary?.morada || "";
        if (!suggestion) return;
        // Preenche se: morada vazia OU morada foi antes definida por este hook
        const currentMorada = (morada || "").trim();
        const previouslySetByHook = lastMoradaSetByHookRef.current;
        const canOverride =
          !userEdited &&
          (currentMorada === "" || currentMorada === previouslySetByHook);
        if (canOverride) {
          setMorada(suggestion);
          lastMoradaSetByHookRef.current = suggestion;
        }
      } catch (err) {
        if (cancelled) return;
        // 404 = CP não existe na base — silencioso; outros erros mostramos.
        const status = err?.response?.status;
        if (status && status !== 404) {
          setError("Não foi possível procurar o código postal.");
        } else {
          setError("");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postalCode, enabled, userEdited]);

  return { loading, error };
}
